import { useState, useMemo, useRef, useEffect } from 'react';
import { isEqual } from 'lodash-es';
import { Wrapper } from '@googlemaps/react-wrapper';
import GooglePlacesAutocomplete, {
  geocodeByPlaceId,
} from 'react-google-places-autocomplete';
import googleMapsWrapperConfig from '../../../config/googleMapsWrapperConfig';
import { useLocation } from '../../../context/location.context';

import Icon from '../../Atoms/Icon/Icon';
import bem from '../../../utils/bem';
import Input from '../../Atoms/FormComponents/Input';

const { supportedLocales, defaultSearchablePlaceTypes, removeZipCookies } =
  window.drupalSettings.sgnaGeolocation;

const GooglePlacesAuto = ({
  distanceOptions,
  onPlaceSelected,
  onDistanceChange,
  className,
  restrictToDefaultRegions = true,
  restrictToDefaultTypes = true,
  inputDefaultValue = '',
  focusOnInput,
  label,
  contextEnabled = true,
  selectedDistance,
  placeholder,
  affectCookie = false,
  hideLabel = false,
  inputIdDefault,
  iconName = 'pin',
}) => {
  const { autocompleteValue, setAutocompleteValue } = useLocation();
  const instanceId = Math.random().toString().slice(-8);
  const [value, setValue] = useState('');
  const [inputTextValue, setInputTextValue] = useState('');
  const [menuIsOpen, setMenuIsOpen] = useState(false);

  useEffect(() => {
    setValue(
      inputDefaultValue
        ? {
            label: inputDefaultValue,
            value: {},
          }
        : null,
    );

    if (inputDefaultValue) {
      setInputTextValue(inputDefaultValue);
    }
  }, [inputDefaultValue]);

  const selectRef = useRef(null);
  const baseClassName = 'm-google-places-auto';
  const useApiMsg = Drupal.t(
    'Please provide a valid Google Maps API key to use the component',
  );
  const placeholderValue =
    placeholder || Drupal.t('Enter ZIP Code, City or State');
  const inputId =
    inputIdDefault ||
    `google-geo-places-input-${Math.random().toString(16).slice(2)}`;

  const autocompletionRequest = useMemo(() => {
    const regionsRestriction =
      restrictToDefaultRegions && supportedLocales
        ? {
            country: Object.keys(supportedLocales),
          }
        : {};
    const typesRestriction =
      restrictToDefaultTypes && defaultSearchablePlaceTypes
        ? defaultSearchablePlaceTypes
        : [];

    return {
      componentRestrictions: regionsRestriction,
      types: typesRestriction,
    };
  }, [restrictToDefaultRegions, restrictToDefaultTypes]);

  // Pass count state to focus on autocomplete.
  useEffect(() => {
    if (focusOnInput && selectRef?.current) {
      selectRef.current.focus();
    }
  }, [focusOnInput]);

  useEffect(() => {
    if (
      contextEnabled &&
      autocompleteValue &&
      !isEqual(autocompleteValue, value)
    ) {
      setValue(autocompleteValue);
    }

    if (window?.ct?.getCookie('USER_ZIP_CODE') && !inputDefaultValue) {
      setInputTextValue(window?.ct?.getCookie('USER_ZIP_CODE'));
    } else if (!inputDefaultValue) {
      setInputTextValue('');
    }
  }, [autocompleteValue]);

  const onChangeHandler = async (val) => {
    try {
      const results = await geocodeByPlaceId(val.value.place_id);
      if (typeof onPlaceSelected === 'function') {
        onPlaceSelected(results);
      }
      if (contextEnabled && val !== value) {
        setAutocompleteValue(val);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }

    if (selectRef?.current) {
      selectRef.current.blur();
    }
  };

  useEffect(() => {
    if (value?.value?.place_id) {
      onChangeHandler(value);
    } else {
      setInputTextValue(
        value?.value?.structured_formatting?.main_text || value?.label || '',
      );
    }
  }, [value]);

  if (!googleMapsWrapperConfig.apiKey) {
    return <p>{useApiMsg}</p>;
  }

  const onTextInputChange = (newValue, actionMeta) => {
    const { action } = actionMeta;

    if (action === 'input-change') {
      setInputTextValue(newValue);
    } else {
      setInputTextValue(
        value?.value?.structured_formatting?.main_text || value?.label || '',
      );
    }

    if (action === 'menu-close') {
      setMenuIsOpen(false);
    } else {
      setMenuIsOpen(true);
    }
  };

  const onGooglePlaceEnter = () => {
    if (inputTextValue !== '' || !removeZipCookies) {
      return;
    }

    if (affectCookie) {
      removeZipCookies();
    }

    if (typeof onPlaceSelected === 'function') {
      onPlaceSelected(null);
    }

    setInputTextValue('');
    setValue('');
    setMenuIsOpen(false);
  };

  return (
    <div className={bem(baseClassName, [], '', className)}>
      <div className={bem('input', [], baseClassName)}>
        <Icon
          iconName={iconName}
          width={18}
          height={24}
          className={bem('icon', [], baseClassName)}
        />
        {!hideLabel && (
          <label className={bem('label', [], baseClassName)} htmlFor={inputId}>
            {label || placeholderValue}
          </label>
        )}
        <span
          className={bem(
            'placeholder-custom',
            [inputTextValue?.length === 0 ? '' : 'hide'],
            baseClassName,
          )}
        >
          {placeholderValue}
        </span>
        <Wrapper {...googleMapsWrapperConfig}>
          <GooglePlacesAutocomplete
            selectProps={{
              value,
              onChange: (newValue) => {
                setValue(newValue);
                setInputTextValue(
                  newValue?.value?.structured_formatting?.main_text || '',
                );
              },
              onKeyDown: (e) => {
                if (e.keyCode === window?.ct?.keyCodes?.ent) {
                  onGooglePlaceEnter(e);
                }
              },
              unstyled: true,
              openMenuOnFocus: false,
              openMenuOnClick: false,
              classNamePrefix: baseClassName,
              inputId,
              ref: selectRef,
              inputValue: inputTextValue,
              onInputChange: onTextInputChange,
              menuIsOpen,
            }}
            autocompletionRequest={autocompletionRequest}
          />
        </Wrapper>
      </div>
      {distanceOptions && (
        <fieldset className={bem('radius', [], baseClassName)}>
          <legend className="visually-hidden">
            {Drupal.t('Select location radius')}
          </legend>
          <div className={bem('radius-options', [], baseClassName)}>
            {distanceOptions.map((opt) => (
              <Input
                key={opt.value + instanceId}
                name={`location-radius + ${instanceId}`}
                type="radio"
                children={opt.label}
                checked={opt.value === selectedDistance}
                onChange={() => onDistanceChange(opt.value)}
              />
            ))}
          </div>
        </fieldset>
      )}
    </div>
  );
};

export default GooglePlacesAuto;
